'use client';

import { Cat, ChevronLeft, ChevronRight, Dog } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import type React from 'react';
import { useTranslations } from 'next-intl';
import { css, cx } from '@/styled-system/css';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import Card from '@/src/components/features/property/ui/Card';
import Carousel from '@/src/components/features/property/ui/Carousel';
import { useCarouselNavigation } from '@/src/components/features/property/ui/Carousel';
import { Module } from '@/src/components/features/property/ui/Module/Module';
import { Tag } from '@zillow/constellation-10';
import { KeyValue } from '@/src/components/features/property/ui/KeyValue';
import { ReadMoreText } from '@/src/components/features/property/lib/ReadMoreText';
import {
  additionalInfo,
  additionalInfoSection,
  infoContent,
  infoLabel,
  petCard,
  petCardItem,
  petCardItemSingle,
  petControlButton,
  petDetails,
  petHeaderControls,
  petHeaderControlsHidden,
  petIcon,
} from './petsModule.styles';

type PetPolicy = {
  allowed: boolean;
  petSize?: string | null;
  deposit: string | number | null;
  oneTimeFee: string | number | null;
  monthlyFee: string | number | null;
  maxNumberAllowed: string | number | null;
  maxWeightAllowed: string | number | null;
  comment: string;
  restriction: string;
};

type PetPolicyMap = {
  dog?: Array<PetPolicy>;
  cat?: Array<PetPolicy>;
};

type SizedPetNameKey =
  | 'tinyDogs'
  | 'smallDogs'
  | 'mediumDogs'
  | 'largeDogs'
  | 'hugeDogs'
  | 'tinyCats'
  | 'smallCats'
  | 'mediumCats'
  | 'largeCats'
  | 'hugeCats';

const SIZED_PET_NAME_KEYS: Record<'dog' | 'cat', Partial<Record<string, SizedPetNameKey>>> = {
  dog: { tiny: 'tinyDogs', small: 'smallDogs', medium: 'mediumDogs', large: 'largeDogs', huge: 'hugeDogs' },
  cat: { tiny: 'tinyCats', small: 'smallCats', medium: 'mediumCats', large: 'largeCats', huge: 'hugeCats' },
};

// Pet-policy fields come from the API as mostly-string values, but some
// listings return numbers (e.g. maxNumberAllowed: 2) instead of "2 dogs".
// Coerce at the boundary so the extractors don't crash on .match().
const asString = (value: string | number | null | undefined): string | null => {
  if (value == null) return null;
  return typeof value === 'string' ? value : String(value);
};

const extractFeeAmount = (feeString: string | number | null): string | null => {
  const s = asString(feeString);
  if (!s) return null;
  const match = s.match(/^\$\d+(?:,\d{3})*(?:\.\d{2})?/);
  if (match) return match[0];
  // The V3 API sends bare numbers (deposit: 750), so add the dollar sign
  // and thousands separators the legacy formatter used to provide. Like the
  // legacy numberUtils.asCurrency, whole dollars drop the decimals while
  // fractional amounts keep their cents ($750, $75.50).
  if (/^\d[\d,]*(?:\.\d+)?$/.test(s)) {
    const numeric = Number(s.replace(/,/g, ''));
    if (Number.isFinite(numeric)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: numeric % 1 === 0 ? 0 : 2,
      }).format(numeric);
    }
  }
  return s;
};

const extractNumberAllowed = (numberString: string | number | null): string | null => {
  const s = asString(numberString);
  if (!s) return null;
  const match = s.match(/^(\d+)/);
  return match ? match[1] : s;
};

const extractWeightLimit = (weightString: string | number | null): string | null => {
  const s = asString(weightString);
  if (!s) return null;
  const match = s.match(/^(\d+\s*lbs?)/);
  return match ? match[1] : s;
};

const isOnlyPetTypeName = (comment: string, petType: 'dog' | 'cat'): boolean => {
  const normalized = comment.trim().toLowerCase();
  return normalized === petType || normalized === `${petType}s`;
};

type PetCardProps = {
  petType: 'dog' | 'cat';
  policy: PetPolicy;
};

const PetCard: React.FC<PetCardProps> = ({ petType, policy }) => {
  const t = useTranslations('hdp');
  const petSize = policy.petSize?.toLowerCase() ?? null;
  const sizedNameKey = petSize ? SIZED_PET_NAME_KEYS[petType][petSize] : undefined;
  const petName = sizedNameKey ? t(sizedNameKey) : petType === 'dog' ? t('dogs') : t('cats');
  const idPrefix = petSize ? `${petType}-${petSize}` : petType;
  const PetIcon = petType === 'dog' ? Dog : Cat;
  const badgeTone = policy.allowed ? 'success' : 'critical';
  const badgeText = policy.allowed ? t('allowed') : t('notAllowed');
  const hasComment = Boolean(policy.comment) && !isOnlyPetTypeName(policy.comment, petType);

  return (
    <Card variant="default" size="sm" className={petCard}>
      <Card.Header layout="space-between">
        <div className={css({ display: 'flex', alignItems: 'center', gap: 'tighter' })}>
          <Card.Icon>
            <span className={petIcon} aria-hidden="true">
              <LucideIcon icon={PetIcon} size="md" />
            </span>
          </Card.Icon>
          <Card.Title size="lg">{petName}</Card.Title>
        </div>
        <Tag tone={badgeTone} size="md">
          {badgeText}
        </Tag>
      </Card.Header>

      {policy.allowed && (
        <Card.Body>
          <div className={petDetails}>
            {policy.deposit && <KeyValue label={t('deposit')} value={extractFeeAmount(policy.deposit)} />}

            {policy.oneTimeFee && <KeyValue label={t('oneTimeFee')} value={extractFeeAmount(policy.oneTimeFee)} />}

            {policy.monthlyFee && (
              <KeyValue
                label={t('monthlyFee')}
                value={t('monthlyFeeValue', { value: extractFeeAmount(policy.monthlyFee) ?? '' })}
              />
            )}

            {policy.maxNumberAllowed && (
              <KeyValue label={t('numberAllowed')} value={extractNumberAllowed(policy.maxNumberAllowed)} />
            )}

            {policy.maxWeightAllowed && (
              <KeyValue label={t('weightLimit')} value={extractWeightLimit(policy.maxWeightAllowed)} />
            )}

            {(policy.restriction || hasComment) && (
              <div className={additionalInfo}>
                {policy.restriction && (
                  <section aria-labelledby={`${idPrefix}-restrictions-heading`}>
                    <h4 className={infoLabel} id={`${idPrefix}-restrictions-heading`}>
                      {t('restrictions')}
                    </h4>
                    <div className={infoContent}>
                      <ReadMoreText
                        controlsId={`${idPrefix}-pet-policy-restrictions`}
                        content={policy.restriction}
                        maxTruncatedLength={100}
                        readMoreText={t('readMore')}
                        readLessText={t('readLess')}
                        aria-label={t('petPolicyRestrictions')}
                      />
                    </div>
                  </section>
                )}

                {hasComment && (
                  <section
                    aria-labelledby={`${idPrefix}-additional-info-heading`}
                    className={policy.restriction ? additionalInfoSection : undefined}
                  >
                    <h4 className={infoLabel} id={`${idPrefix}-additional-info-heading`}>
                      {t('additionalInformation')}
                    </h4>
                    <div className={infoContent}>
                      <ReadMoreText
                        controlsId={`${idPrefix}-pet-policy-additional-info`}
                        content={policy.comment}
                        maxTruncatedLength={100}
                        readMoreText={t('readMore')}
                        readLessText={t('readLess')}
                        aria-label={t('petPolicyAdditional')}
                      />
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

type Props = {
  listing: PropertyDetailListing;
};

/**
 * PetsModule — horizontal carousel of pet policy cards (Dogs / Cats).
 * Owns its Module chrome (componentOwnsChrome) so the desktop prev/next
 * controls can sit in the title row next to the "Pets" heading.
 */
export const PetsModule: React.FC<Props> = ({ listing }) => {
  const t = useTranslations('hdp');
  const { trackRef, canScrollLeft, canScrollRight, totalPages, handlePrevious, handleNext, handleScroll } =
    useCarouselNavigation({ gap: 'md', step: 1 });
  const petPolicies = (listing?.amenities?.petPolicies ?? null) as PetPolicyMap | null;
  const cards = [
    ...(petPolicies?.dog ?? []).map((policy) => ({ petType: 'dog' as const, policy })),
    ...(petPolicies?.cat ?? []).map((policy) => ({ petType: 'cat' as const, policy })),
  ];

  if (cards.length === 0) {
    return null;
  }

  const itemClassName = cards.length > 1 ? petCardItem : petCardItemSingle;
  const showControls = totalPages > 1;

  // Always mounted (visibility-gated) to avoid post-measure layout shift; aria-disabled keeps focus at the extremes.
  const headerControls = (
    <div className={cx(petHeaderControls, !showControls && petHeaderControlsHidden)}>
      <button
        type="button"
        className={petControlButton}
        onClick={canScrollLeft ? handlePrevious : undefined}
        aria-disabled={!canScrollLeft}
        aria-label={t('previousPetPolicies')}
      >
        <LucideIcon icon={ChevronLeft} size="sm" />
      </button>
      <button
        type="button"
        className={petControlButton}
        onClick={canScrollRight ? handleNext : undefined}
        aria-disabled={!canScrollRight}
        aria-label={t('nextPetPolicies')}
      >
        <LucideIcon icon={ChevronRight} size="sm" />
      </button>
    </div>
  );

  return (
    <Module id="pets-module" title={t('petsTitle')} headerActions={headerControls}>
      <Carousel.Track ref={trackRef} gap="md" snapAlign="start" onScroll={handleScroll} aria-label={t('petPolicyInfo')}>
        {cards.map(({ petType, policy }) => (
          <Carousel.Item key={`${petType}-${policy.petSize?.toLowerCase() ?? 'any'}`} className={itemClassName}>
            <PetCard petType={petType} policy={policy} />
          </Carousel.Item>
        ))}
      </Carousel.Track>
    </Module>
  );
};
